import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
  Factory,
  Truck,
} from 'lucide-react';
import subcontractService from '../../services/subcontractService';

const SubcontractCompleteDialog = ({
  open,
  onClose,
  order,
  onSuccess
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [itemCompletions, setItemCompletions] = useState([]);
  const [remainingBars, setRemainingBars] = useState([]);
  
  const [showAddRemaining, setShowAddRemaining] = useState(false);
  const [newRemainingBar, setNewRemainingBar] = useState({
    length: '',
    quantity: 1,
    location: 'main',
    leftover_type: 'blank',
    tip_diameter: ''
  });

  // Initialize item completions from order items
  useEffect(() => {
    if (order?.items) {
      setItemCompletions(
        order.items.map(item => ({
          item_id: item.item_id,
          completed_quantity: item.planned_quantity || 0,
          scrap_quantity: 0,
          returned_quantity: 0
        }))
      );
    }
    setRemainingBars([]);
    setError(null);
    setShowAddRemaining(false);
    setNewRemainingBar({
      length: '',
      quantity: 1,
      location: 'main',
      leftover_type: 'blank',
      tip_diameter: ''
    });
  }, [order, open]);

  const updateItemCompletion = (itemId, field, value) => {
    setItemCompletions(prev =>
      prev.map(item =>
        item.item_id === itemId ? { ...item, [field]: parseFloat(value) || 0 } : item
      )
    );
  };

  const handleAddRemainingBar = () => {
    if (!newRemainingBar.length || parseFloat(newRemainingBar.length) <= 0) {
      setError('请输入有效的余料长度');
      return;
    }
    if (newRemainingBar.leftover_type === 'ground' && !newRemainingBar.tip_diameter) {
      setError('精磨余料需输入刃径');
      return;
    }

    setRemainingBars([...remainingBars, {
      ...newRemainingBar,
      id: `sc-lo-${Date.now()}`,
      length: parseFloat(newRemainingBar.length),
      quantity: parseInt(newRemainingBar.quantity) || 1,
      tip_diameter: newRemainingBar.leftover_type === 'ground' 
        ? (parseFloat(newRemainingBar.tip_diameter) || 0)
        : 0
    }]);

    setNewRemainingBar({
      length: '',
      quantity: 1,
      location: 'main',
      leftover_type: 'blank',
      tip_diameter: ''
    });
    setShowAddRemaining(false);
    setError(null);
  };

  const removeRemainingBar = (id) => {
    setRemainingBars(remainingBars.filter(bar => bar.id !== id));
  };

  const handleSubmit = async () => {
    if (!order) return;
    
    setSubmitting(true);
    setError(null);

    try {
      const leftoverData = remainingBars.map(bar => ({
        length: bar.length,
        quantity: bar.quantity,
        location: bar.location,
        leftover_type: bar.leftover_type,
        tip_diameter: bar.leftover_type === 'ground' ? bar.tip_diameter : undefined
      }));

      const result = await subcontractService.receiveCompletion(
        order.subcontract_order_id,
        itemCompletions,
        null,
        null,
        leftoverData.length > 0 ? leftoverData : undefined
      );
      
      onSuccess?.(result);
      handleClose();
    } catch (err) {
      console.error('Failed to complete subcontract order:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || '完成外协单失败';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setRemainingBars([]);
    setShowAddRemaining(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Factory className="h-5 w-5" />
            确认外协单完成
          </DialogTitle>
          <DialogDescription>
            外协单 {order?.subcontract_order_id} - {order?.supplier_name} - {order?.step_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Info */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="space-y-1">
              <Label className="text-muted-foreground">供应商</Label>
              <p className="font-medium">{order?.supplier_name}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-muted-foreground">工序</Label>
              <p className="font-medium">{order?.step_name}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-muted-foreground">计划数量</Label>
              <p className="font-medium">{order?.total_planned_quantity}</p>
            </div>
          </div>

          {/* Item Completions */}
          <Card>
            <div className="p-4">
              <Label className="text-base font-medium mb-3 block">完成数量确认</Label>
              <Table>
                <TableHeader>
                  <TableRow>
                    {order?.is_manual ? (
                      <>
                        <TableHead>产品编码</TableHead>
                        <TableHead>产品名称</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead>工单号</TableHead>
                        <TableHead>产品编码</TableHead>
                      </>
                    )}
                    <TableHead className="text-right">计划数量</TableHead>
                    <TableHead className="text-right">完成数量</TableHead>
                    <TableHead className="text-right">报废数量</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itemCompletions.map((item, idx) => (
                    <TableRow key={item.item_id}>
                      {order?.is_manual ? (
                        <>
                          <TableCell className="text-sm font-mono">
                            {order?.items?.[idx]?.product_code || order?.items?.[idx]?.finished_product_code || '-'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {order?.items?.[idx]?.product_name || order?.items?.[idx]?.finished_product_name || '-'}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-sm">
                            {order?.items?.[idx]?.production_order_number || '-'}
                          </TableCell>
                          <TableCell className="text-sm font-mono">
                            {order?.items?.[idx]?.finished_product_code || '-'}
                          </TableCell>
                        </>
                      )}
                      <TableCell className="text-right">
                        {order?.items?.[idx]?.planned_quantity || 0}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          value={item.completed_quantity}
                          onChange={(e) => updateItemCompletion(item.item_id, 'completed_quantity', e.target.value)}
                          className="w-20 h-8 text-right"
                          disabled={submitting}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          value={item.scrap_quantity}
                          onChange={(e) => updateItemCompletion(item.item_id, 'scrap_quantity', e.target.value)}
                          className="w-20 h-8 text-right"
                          disabled={submitting}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Leftover Entry */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-muted-foreground" />
                <Label className="text-base font-medium">余料入库</Label>
                <Badge variant="outline" className="text-xs">可选</Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddRemaining(true)}
                disabled={submitting || showAddRemaining}
              >
                <Plus className="h-4 w-4 mr-1" />
                添加余料
              </Button>
            </div>

            {showAddRemaining && (
              <Card className="p-3 border-dashed">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">余料类型</Label>
                    <Select
                      value={newRemainingBar.leftover_type}
                      onValueChange={(value) => {
                        setNewRemainingBar({
                          ...newRemainingBar,
                          leftover_type: value,
                          tip_diameter: value === 'blank' ? '' : newRemainingBar.tip_diameter
                        });
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="选择余料类型" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blank">毛坯余料</SelectItem>
                        <SelectItem value="ground">精磨余料</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">长度(mm)</Label>
                      <Input
                        type="number"
                        value={newRemainingBar.length}
                        onChange={(e) => setNewRemainingBar({...newRemainingBar, length: e.target.value})}
                        placeholder="长度"
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">数量(根)</Label>
                      <Input
                        type="number"
                        value={newRemainingBar.quantity}
                        onChange={(e) => setNewRemainingBar({...newRemainingBar, quantity: e.target.value})}
                        placeholder="数量"
                        className="h-8"
                      />
                    </div>
                  </div>

                  {newRemainingBar.leftover_type === 'ground' && (
                    <div className="space-y-1">
                      <Label className="text-xs">刃径(mm)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newRemainingBar.tip_diameter}
                        onChange={(e) => setNewRemainingBar({...newRemainingBar, tip_diameter: e.target.value})}
                        placeholder="精磨后刃径"
                        className="h-8"
                      />
                      <p className="text-[10px] text-muted-foreground">精磨余料需手动输入实际刃径</p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label className="text-xs">位置</Label>
                    <Input
                      value={newRemainingBar.location}
                      onChange={(e) => setNewRemainingBar({...newRemainingBar, location: e.target.value})}
                      placeholder="位置"
                      className="h-8"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowAddRemaining(false)}>取消</Button>
                    <Button size="sm" onClick={handleAddRemainingBar}>确认添加</Button>
                  </div>
                </div>
              </Card>
            )}

            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>类型</TableHead>
                    <TableHead>长度</TableHead>
                    <TableHead>数量</TableHead>
                    <TableHead>库位</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {remainingBars.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-4">
                        无余料记录
                      </TableCell>
                    </TableRow>
                  ) : (
                    remainingBars.map((bar) => (
                      <TableRow key={bar.id}>
                        <TableCell>
                          <Badge variant={bar.leftover_type === 'ground' ? 'default' : 'secondary'} className="text-xs">
                            {bar.leftover_type === 'ground' ? '精磨' : '毛坯'}
                          </Badge>
                          {bar.leftover_type === 'ground' && bar.tip_diameter > 0 && (
                            <span className="text-xs text-blue-600 block">刃径 {bar.tip_diameter}mm</span>
                          )}
                        </TableCell>
                        <TableCell>{bar.length}mm</TableCell>
                        <TableCell>{bar.quantity}</TableCell>
                        <TableCell>{bar.location}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => removeRemainingBar(bar.id)}
                            disabled={submitting}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={submitting}>
              取消
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              确认完成
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubcontractCompleteDialog;
